import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Settings } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/hooks/useAuth';

const PRESET_COLORS = [
  { name: 'Blue', value: '#2563eb' },
  { name: 'Indigo', value: '#4f46e5' },
  { name: 'Purple', value: '#7c3aed' },
  { name: 'Violet', value: '#9333ea' },
  { name: 'Pink', value: '#db2777' },
  { name: 'Rose', value: '#e11d48' },
  { name: 'Orange', value: '#ea580c' },
  { name: 'Amber', value: '#d97706' },
  { name: 'Green', value: '#059669' },
  { name: 'Teal', value: '#0891b2' },
  { name: 'Cyan', value: '#0891b2' },
  { name: 'Sky', value: '#0284c7' },
];

export default function TeamSettingsDialog({ open, onClose, team, onUpdate }) {
  const [formData, setFormData] = useState({
    name: '',
    color: '#2563eb'
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (team) {
      setFormData({
        name: team.name,
        color: team.color || '#2563eb'
      });
    }
  }, [team]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Team name cannot be empty');
      return;
    }

    setLoading(true);
    try {
      await api.put(`/teams/${team.id}`, formData);
      toast.success('Team updated successfully');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update team');
    } finally {
      setLoading(false);
    }
  };

  if (!team) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]" data-testid="team-settings-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#0f172a] flex items-center gap-2">
            <Settings size={24} />
            Team Settings
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="team-name">Team Name *</Label>
              <Input
                id="team-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter team name"
                required
                data-testid="team-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Team Color</Label>
              <div className="grid grid-cols-6 gap-2">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, color: color.value })}
                    className={`w-10 h-10 rounded-lg border-2 transition-all ${
                      formData.color === color.value
                        ? 'border-[#0f172a] scale-110 shadow-md'
                        : 'border-slate-200 hover:border-slate-400'
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                    data-testid={`color-${color.name.toLowerCase()}`}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Label htmlFor="custom-color" className="text-sm">Custom:</Label>
                <input
                  id="custom-color"
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="h-10 w-20 rounded border border-slate-200 cursor-pointer"
                  data-testid="custom-color-picker"
                />
                <span className="text-sm text-slate-600 font-mono">{formData.color}</span>
              </div>
            </div>

            <div className="border-t pt-4 mt-4">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold"
                  style={{ backgroundColor: formData.color }}
                >
                  {formData.name.charAt(0).toUpperCase() || 'T'}
                </div>
                <div>
                  <p className="font-semibold text-[#0f172a]">{formData.name || 'Team Name'}</p>
                  <p className="text-sm text-slate-600">Preview</p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid="cancel-button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
              disabled={loading}
              data-testid="save-team-button"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
